import { z } from "zod";

/**
 * LE JOURNAL D'AUDIT : le format d'un evenement (`orgs/{orgId}/auditEvents/{id}`).
 *
 * Un evenement repond a « qui a fait quoi, quand, sur quoi, avec quel
 * resultat ». Il est ECRIT par les Functions seules (Admin SDK) et LU par le
 * proprietaire de l'environnement seul (rules). Le mode d'emploi, les
 * categories et les trois interdits vivent dans `docs/JOURNAL.md`.
 *
 * CE FICHIER EST RECOPIE dans `functions/src/journal/schema.ts` : `functions/`
 * ne depend d'aucun `@taki/*` (`docs/FUNCTIONS.md`). Les constantes ci-dessous
 * tiennent chacune sur UNE ligne, parce que
 * `packages/sdk/tests/journal-contrat.test.mjs` lit les deux fichiers et echoue
 * si elles se separent.
 *
 * C'EST AUSSI LA SOURCE DES NOTIFICATIONS : le moteur de notifications
 * s'abonne a la creation d'un evenement et ne pose pas ses propres triggers.
 * Changer la forme d'un evenement, c'est donc changer deux consommateurs :
 * `schemaVersion` monte, et l'ancien format reste lisible.
 *
 * LES TROIS INTERDITS, que le schema tient autant qu'un schema le peut : le
 * TEXTE d'une question a Lia ou de sa reponse, le CONTENU d'une fiche, un
 * SECRET (mot de passe, clef, jeton). Pas d'adresse IP non plus en v1. Les
 * valeurs avant/apres d'un changement ne passent que pour les champs de
 * `AUDIT_CHAMPS_A_VALEUR` ; pour tout autre champ, seul son NOM est garde.
 */

/** Version de la forme d'un evenement. */
export const AUDIT_SCHEMA_VERSION = 1;

/** La sous-collection d'une org qui porte le journal. */
export const AUDIT_EVENTS_COLLECTION = "auditEvents";

/** Duree de conservation, en jours (decision du fondateur, 04/10/2026 : 12 mois). */
export const AUDIT_RETENTION_JOURS = 365;

/** Les familles d'evenements. Le premier segment d'une action est sa categorie. */
export const AUDIT_CATEGORIES = ["auth", "settings", "access", "ia", "record", "app"] as const;

/** D'ou vient le geste : l'ecran, Lia, un client MCP, une Function, une tache planifiee, un script. */
export const AUDIT_SOURCES = ["web", "ask", "mcp", "lia", "automatisation", "function", "scheduler", "script"] as const;

/** Le resultat du geste. */
export const AUDIT_OUTCOMES = ["ok", "denied", "error"] as const;

/** Les SEULS champs dont un changement garde la valeur avant/apres. */
export const AUDIT_CHAMPS_A_VALEUR = ["enabled", "model", "launcher", "orgRole", "disabled"] as const;

/** Taille maximale de `meta`, en octets de JSON. */
export const AUDIT_META_MAX_OCTETS = 1024;

/** Nombre maximal de lignes de `changes` (au-dela : la derniere dit `...`). */
export const AUDIT_CHANGES_MAX = 40;

/**
 * Le nom d'une action : `categorie.objet.verbe`, deux a quatre segments en
 * minuscules, chiffres et tirets. Les apps ecrivent `app.<appId>.<verbe>`.
 */
export const AUDIT_ACTION_MOTIF = "^(auth|settings|access|ia|record|app)(\\.[a-z0-9][a-z0-9-]{0,39}){1,3}$";

/**
 * LE CATALOGUE DES ACTIONS DU SOCLE. Une action d'app n'y figure pas : elle
 * suit `app.<appId>.<verbe>`. Une action nouvelle du socle s'ajoute ici, dans
 * la recopie serveur, et dans le tableau de `docs/JOURNAL.md`.
 */
export const AUDIT_ACTIONS_SOCLE = ["auth.signin", "auth.claims.set", "settings.ia.update", "settings.branding.update", "settings.branding.reset", "settings.apps.install", "settings.apps.uninstall", "settings.environnement.update", "settings.app.update", "settings.app.reset", "settings.market-key.set", "settings.market-key.remove", "access.member.create", "access.member.update", "access.member.archive", "access.member.restore", "access.account.create", "access.account.password", "access.account.remove", "access.ia-connection.create", "access.ia-connection.revoke", "access.ia-connection.update", "access.connexion.connect", "access.connexion.update", "access.connexion.remove", "ia.ask", "ia.tool", "ia.studio.apply"] as const;

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];
export type AuditSource = (typeof AUDIT_SOURCES)[number];
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];
export type AuditActionSocle = (typeof AUDIT_ACTIONS_SOCLE)[number];

/** Une valeur gardee dans le journal : un scalaire, jamais un objet. */
export const auditScalaireSchema = z.union([z.string().max(200), z.number(), z.boolean(), z.null()]);

/** Qui a fait le geste. `uid` nul : un geste sans compte (tache planifiee, script). */
export const AuditActorSchema = z.object({
  uid: z.string().min(1).max(128).nullable(),
  email: z.string().max(320).nullable(),
  role: z.string().max(20).nullable(),
  source: z.enum(AUDIT_SOURCES),
});

/** Sur quoi porte le geste. Absent : le geste porte sur l'environnement entier (une connexion). */
export const AuditTargetSchema = z.object({
  collection: z.string().min(1).max(80),
  id: z.string().min(1).max(200),
  appId: z.string().max(64).optional(),
  door: z.string().max(64).optional(),
  label: z.string().max(120).optional(),
});

/** Un champ change. `before`/`after` seulement pour `AUDIT_CHAMPS_A_VALEUR`. */
export const AuditChangeSchema = z.object({
  field: z.string().min(1).max(80),
  before: auditScalaireSchema.optional(),
  after: auditScalaireSchema.optional(),
});

/** Des precisions PLATES (pas d'objet imbrique), bornees a `AUDIT_META_MAX_OCTETS`. */
export const AuditMetaSchema = z.record(z.string().min(1).max(40), auditScalaireSchema);

/**
 * L'evenement tel qu'on le LIT (dates en `Date`, conversion faite par le SDK).
 * `at` est l'horodatage du serveur ; `expiresAt` porte la politique TTL de
 * Firestore, qui supprime le document passe cette date (jusqu'a 24 h apres).
 */
export const AuditEventSchema = z.object({
  id: z.string().min(1),
  schemaVersion: z.literal(AUDIT_SCHEMA_VERSION),
  orgId: z.string().min(1),
  at: z.date(),
  expiresAt: z.date(),
  category: z.enum(AUDIT_CATEGORIES),
  action: z.string().regex(new RegExp(AUDIT_ACTION_MOTIF)),
  actor: AuditActorSchema,
  target: AuditTargetSchema.nullable(),
  changes: z.array(AuditChangeSchema).max(AUDIT_CHANGES_MAX).optional(),
  meta: AuditMetaSchema.optional(),
  outcome: z.enum(AUDIT_OUTCOMES),
});

export type AuditActor = z.infer<typeof AuditActorSchema>;
export type AuditTarget = z.infer<typeof AuditTargetSchema>;
export type AuditChange = z.infer<typeof AuditChangeSchema>;
export type AuditMeta = z.infer<typeof AuditMetaSchema>;
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/** La categorie d'une action : son premier segment. */
export function categorieDAction(action: string): AuditCategory | null {
  const tete = action.split(".")[0];
  return (AUDIT_CATEGORIES as readonly string[]).includes(tete ?? "") ? (tete as AuditCategory) : null;
}

/** Le nom d'action qu'une app doit porter : `app.<appId>.<verbe>`, ou `null` s'il est invalide. */
export function actionDApp(appId: string, verbe: string): string | null {
  const action = `app.${appId}.${verbe}`;
  return new RegExp(AUDIT_ACTION_MOTIF).test(action) && action.split(".").length === 3 ? action : null;
}
